import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link as RouterLink, Navigate } from 'react-router-dom'
import { Loader, Banner, Text, Button, useKumoToastManager } from '@cloudflare/kumo'
import { IdentificationCardIcon } from '@phosphor-icons/react'
import { apiRequest, ApiError } from '../api/client'
import { listGuestInvitations, listRecentGuests } from '../api/guestInvitations'
import { useTranslation } from '../i18n'
import { useEnrollmentCompleteness } from '../hooks/useEnrollmentCompleteness'
import { useMyFaceStatus } from '../hooks/useMyFaceStatus'
import { FaceStatusBanner } from '../components/face-status/FaceStatusBanner'
import { AboutBuilding } from '../components/home/AboutBuilding'
import { BookSheet } from '../components/home/BookSheet'
import { FreeNowStrip } from '../components/home/FreeNowStrip'
import { HomeAgenda } from '../components/home/HomeAgenda'
import { HomeGreeting } from '../components/home/HomeGreeting'
import { InviteAgain } from '../components/home/InviteAgain'
import { InviteSheet } from '../components/home/InviteSheet'
import { NextUpCard } from '../components/home/NextUpCard'
import { QuickActions } from '../components/home/QuickActions'
import { buildAgenda, pickNextUp } from '../components/home/agenda'
import { formatHourMinute } from '../lib/calendar-time'
import '../components/home/home.css'
import type {
  Building,
  Room,
  GuestInvitationDto,
  RecentGuest,
  RoomBookingDto,
} from '@hub-wifi/shared'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface BuildingWithRooms extends Building {
  rooms: Room[]
}

interface MyBuildingResponse {
  building: BuildingWithRooms | null
}

interface MyBookingsResponse {
  items: RoomBookingDto[]
}

type AuthMeResponse =
  | { status: 'no_account'; email: string; roles: string[] }
  | {
      status: 'active'
      coworker: { id: string; name: string; email: string; companyId: string | null }
      hasWifiCredential: boolean
      wifiUsername: string | null
      roles: string[]
    }

/** Enough for the Next-up card plus a ~6-row agenda after nesting. */
const BOOKINGS_FETCH_LIMIT = 20

interface AgendaData {
  bookings: RoomBookingDto[]
  invitations: GuestInvitationDto[]
  recentGuests: RecentGuest[]
}

/** Bookings, invitations and recent guests. Best-effort: a failing list
 *  hides its rows, not the whole home. */
async function fetchAgenda(token: string): Promise<AgendaData> {
  const bookingsQs = new URLSearchParams({
    timeframe: 'upcoming',
    limit: String(BOOKINGS_FETCH_LIMIT),
  })
  const [bookingsRes, invitations, recentGuests] = await Promise.all([
    apiRequest<MyBookingsResponse>(`/api/my/bookings?${bookingsQs}`, { token }).catch(() => ({
      items: [] as RoomBookingDto[],
    })),
    listGuestInvitations(token).catch(() => [] as GuestInvitationDto[]),
    listRecentGuests(token).catch(() => [] as RecentGuest[]),
  ])
  return { bookings: bookingsRes.items ?? [], invitations, recentGuests }
}

interface InviteContext {
  booking: RoomBookingDto | null
  guest: RecentGuest | null
}

/** Re-render once a minute so "ends in / starts in" and open/closed stay true.
 *  Background tabs throttle timers, so also refresh when the tab comes back. */
function useNow(intervalMs = 60_000): Date {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const tick = () => setNow(new Date())
    const id = window.setInterval(tick, intervalMs)
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [intervalMs])
  return now
}

function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

/* ------------------------------------------------------------------ */
/*  DashboardPage — coworker/operator home (admins get CompaniesPage)  */
/* ------------------------------------------------------------------ */

export function DashboardPage() {
  const { getAccessTokenSilently, user } = useAuth0()
  const { t } = useTranslation()
  // Task 4 (post-login routing for incomplete coworkers): the hook itself
  // gates on role 'user' and fails safe (never nags) on 404/error/other
  // roles — the page trusts it as-is rather than re-deriving the gate.
  const { incomplete: enrollmentIncomplete } = useEnrollmentCompleteness()
  const faceStatus = useMyFaceStatus()
  const now = useNow()
  const [building, setBuilding] = useState<BuildingWithRooms | null>(null)
  const [bookings, setBookings] = useState<RoomBookingDto[]>([])
  const [invitations, setInvitations] = useState<GuestInvitationDto[]>([])
  const [recentGuests, setRecentGuests] = useState<RecentGuest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const toasts = useKumoToastManager()
  const [bookOpen, setBookOpen] = useState(false)
  const [bookSession, setBookSession] = useState(0)
  const [bookedCount, setBookedCount] = useState(0)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteSession, setInviteSession] = useState(0)
  const [inviteContext, setInviteContext] = useState<InviteContext>({
    booking: null,
    guest: null,
  })

  function applyAgenda(data: AgendaData) {
    setBookings(data.bookings)
    setInvitations(data.invitations)
    setRecentGuests(data.recentGuests)
  }

  /** Re-read the agenda after a sheet created something (Next up follows). */
  const refreshAgenda = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently()
      applyAgenda(await fetchAgenda(token))
    } catch {
      // Keep what's on screen; the next visit reloads.
    }
  }, [getAccessTokenSilently])

  useEffect(() => {
    async function load() {
      try {
        const token = await getAccessTokenSilently()

        // Fire login-callback to ensure coworker exists, then validate auth state.
        await apiRequest('/api/auth/login-callback', { method: 'POST', token }).catch(() => {})

        const me = await apiRequest<AuthMeResponse>('/api/auth/me', { token })
        if (me.status === 'no_account') {
          setError('NO_COWORKER')
          return
        }
        if (!me.coworker.companyId) {
          setError('NO_COMPANY')
          return
        }

        // Operators get the same agenda as coworkers (both roles may call the
        // guest-invitation endpoints).
        const [buildingRes, agendaRes] = await Promise.all([
          apiRequest<MyBuildingResponse>('/api/my/building', { token }),
          fetchAgenda(token),
        ])

        setBuilding(buildingRes.building)
        applyAgenda(agendaRes)
      } catch (err) {
        if (err instanceof ApiError && err.status === 403) {
          setError('NOT_ENROLLED')
        } else {
          setError(err instanceof ApiError ? err.message : 'GENERIC_ERROR')
        }
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [getAccessTokenSilently])

  const timeZone = building?.timezone || browserTimeZone()
  const nextUp = useMemo(
    () => pickNextUp(bookings, invitations, timeZone, now),
    [bookings, invitations, timeZone, now],
  )
  const agenda = useMemo(
    () =>
      buildAgenda(bookings, invitations, timeZone, now, { excludeBookingId: nextUp?.booking.id }),
    [bookings, invitations, timeZone, now, nextUp],
  )

  function openBook() {
    setBookSession((n) => n + 1)
    setBookOpen(true)
  }

  function openInvite(context: InviteContext) {
    setInviteContext(context)
    setInviteSession((n) => n + 1)
    setInviteOpen(true)
  }

  function onBooked(dto: RoomBookingDto) {
    setBookOpen(false)
    setBookedCount((n) => n + 1)
    const tz = building?.timezone || browserTimeZone()
    toasts.add({
      variant: 'success',
      title: t('roomBrowse.bookingCreated'),
      description: `${dto.roomName ?? ''} · ${formatHourMinute(dto.startAt, tz)} – ${formatHourMinute(dto.endAt, tz)}`,
      timeout: 10_000,
      actions: [
        {
          children: t('roomBrowse.inviteGuest'),
          variant: 'secondary',
          size: 'sm',
          onClick: () => openInvite({ booking: dto, guest: null }),
        },
      ],
    })
    void refreshAgenda()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader size="lg" />
      </div>
    )
  }

  // Authenticated-but-unprovisioned dead-ends redirect to the shared auth
  // error page (CallbackPage), which carries the localized message plus the
  // Log out escape a stuck user needs. Transient errors stay inline.
  if (error === 'NO_COWORKER' || error === 'NOT_ENROLLED') {
    return <Navigate to="/callback?error=not_provisioned" replace />
  }
  if (error === 'NO_COMPANY') {
    return <Navigate to="/callback?error=no_company" replace />
  }
  if (error) {
    return <Banner variant="error">{t('common.error')}</Banner>
  }

  if (!building) {
    return (
      <div className="py-8 text-center">
        <Text variant="secondary">{t('dashboard.noBuilding')}</Text>
      </div>
    )
  }

  const userName = user?.given_name ?? user?.name ?? user?.email ?? ''
  const firstName = userName.split(/[\s@]/)[0] ?? userName

  return (
    <>
      <HomeGreeting firstName={firstName} hours={building.hours} timeZone={timeZone} now={now} />

      <FaceStatusBanner status={faceStatus} />

      {enrollmentIncomplete && (
        <div className="mb-3">
          <Banner variant="default">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <Text as="p" DANGEROUS_className="font-medium">
                  {t('dashboard.enrollmentCta.title')}
                </Text>
                <Text as="p" variant="secondary" size="sm">
                  {t('dashboard.enrollmentCta.description')}
                </Text>
              </div>
              <RouterLink to="/my/enroll">
                <Button variant="primary" size="sm" icon={<IdentificationCardIcon size={16} />}>
                  {t('dashboard.enrollmentCta.button')}
                </Button>
              </RouterLink>
            </div>
          </Banner>
        </div>
      )}

      {/* Source order = mobile reading order; desktop places the blocks into
          two columns with grid placement only (see home.css). */}
      <div className={nextUp ? 'home-layout' : 'home-layout home-layout--no-next'}>
        {nextUp && (
          <div className="home-area-next">
            <NextUpCard
              nextUp={nextUp}
              onInviteGuest={(booking) => openInvite({ booking, guest: null })}
            />
          </div>
        )}
        <div className="home-area-actions">
          <QuickActions
            onBookRoom={openBook}
            onInviteGuest={() => openInvite({ booking: nextUp?.booking ?? null, guest: null })}
          />
          <FreeNowStrip
            timeZone={timeZone}
            now={now}
            refreshKey={bookedCount}
            onBooked={onBooked}
            onMoreOptions={openBook}
          />
        </div>
        <div className="home-area-agenda">
          <HomeAgenda
            agenda={agenda}
            hasNextUp={nextUp !== null}
            onInviteToBooking={(booking) => openInvite({ booking, guest: null })}
          />
        </div>
        {recentGuests.length > 0 && (
          <div className="home-area-invite">
            <InviteAgain
              guests={recentGuests}
              onInvite={(guest) => openInvite({ booking: nextUp?.booking ?? null, guest })}
            />
          </div>
        )}
        <div className="home-area-about">
          <AboutBuilding building={building} timeZone={timeZone} now={now} />
        </div>
      </div>

      <BookSheet
        key={`book-${bookSession}`}
        open={bookOpen}
        onOpenChange={setBookOpen}
        timeZone={timeZone}
        hours={building.hours}
        now={now}
        onBooked={onBooked}
      />
      <InviteSheet
        key={`invite-${inviteSession}`}
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        timeZone={timeZone}
        now={now}
        booking={inviteContext.booking}
        initialGuest={inviteContext.guest}
        recentGuests={recentGuests}
        onCreated={() => void refreshAgenda()}
      />
    </>
  )
}
